import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Investment } from '../../types/Investment';
import { StandardButton } from '../ui';

interface InvestmentTableRowProps {
  investment: Investment;
  isSelected: boolean;
  onEdit: (investment: Investment) => void;
  onDelete: (id: string) => void;
  onToggle?: (id: string, enabled: boolean) => void;
  onToggleSelection: (id: string) => void;
  useScenarioInvestments: boolean;
  formatCost: (cost: number) => string;
}

export const InvestmentTableRow: React.FC<InvestmentTableRowProps> = React.memo(({
  investment,
  isSelected,
  onEdit,
  onDelete,
  onToggle,
  onToggleSelection,
  useScenarioInvestments,
  formatCost
}) => {
  const { isDark } = useTheme();

  return (
    <tr className={`${
      isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-gray-50'
    }`}>
      <td className="px-6 py-4 whitespace-nowrap">
        {useScenarioInvestments && onToggle ? (
          <input
            type="checkbox"
            checked={investment.isActive}
            onChange={(e) => onToggle(investment.id, e.target.checked)}
            className={`w-3 h-3 rounded focus:ring-2 ${
              isDark 
                ? 'border-white/20 text-professional-blue-400 focus:ring-professional-blue-400' 
                : 'border-gray-300 text-blue-600 focus:ring-blue-500'
            }`}
            style={{ transform: 'scale(0.5)', transformOrigin: 'center' }}
          />
        ) : (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelection(investment.id)}
            className={`w-3 h-3 rounded focus:ring-2 ${
              isDark 
                ? 'border-white/20 text-professional-blue-400 focus:ring-professional-blue-400' 
                : 'border-gray-300 text-blue-600 focus:ring-blue-500'
            }`}
            style={{ transform: 'scale(0.5)', transformOrigin: 'center' }}
          />
        )}
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center">
          <span className="text-2xl mr-3">{investment.icon}</span>
          <div>
            <div className={`text-sm font-medium ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {investment.name}
            </div>
            <div className={`text-sm max-w-xs truncate ${
              isDark ? 'text-slate-400' : 'text-gray-500'
            }`}>
              {investment.description}
            </div>
          </div>
        </div>
      </td>
      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
        isDark ? 'text-green-400' : 'text-green-600'
      }`}>
        {formatCost(investment.cost)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          investment.isActive 
            ? isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-800' 
            : isDark ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-800'
        }`}>
          {investment.isActive ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end gap-2">
          <StandardButton
            type="button"
            onClick={() => onEdit(investment)}
            variant="ghost"
            size="small"
            className="!p-2"
            title="Edit investment"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
            </svg>
          </StandardButton>
          <StandardButton
            type="button"
            onClick={() => onDelete(investment.id)}
            variant="ghost"
            size="small"
            className="!p-2 !text-red-400 hover:!text-red-300"
            title="Delete investment"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
          </StandardButton>
        </div>
      </td>
    </tr>
  );
}, (prevProps, nextProps) => {
  // Custom comparison for better performance
  return (
    prevProps.investment.id === nextProps.investment.id &&
    prevProps.investment.name === nextProps.investment.name &&
    prevProps.investment.description === nextProps.investment.description &&
    prevProps.investment.icon === nextProps.investment.icon &&
    prevProps.investment.cost === nextProps.investment.cost &&
    prevProps.investment.isActive === nextProps.investment.isActive &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.useScenarioInvestments === nextProps.useScenarioInvestments &&
    prevProps.onEdit === nextProps.onEdit &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.onToggle === nextProps.onToggle &&
    prevProps.onToggleSelection === nextProps.onToggleSelection
  );
});

InvestmentTableRow.displayName = 'InvestmentTableRow';